import { Injectable, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { ApiCallingV2Service, API_CONFIG, DropdownData } from '@zayan-erp/shared';
import {
  DsmCreateDeposit,
  DsmCreateDepositPayment,
  DsmDeposit,
  DsmDepositLots,
  DsmDepositPayment,
  DsmDepositSummary,
  DsmDepositType,
  DsmImportDeposit
} from '../models/dsm-deposit.models';

@Injectable({ providedIn: 'root' })
export class DsmDepositService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #baseUrl = this.#apiConfig.baseUrlV2;

  dealId = signal<string | undefined>(undefined);
  lotId = signal<string | undefined>(undefined);

  addPayment = false;
  depositId: string | null = null;
  paymentId: string | null = null;
  addNewDeposits = false;

  deposits = signal<DsmImportDeposit[]>([]);

  form = new FormGroup({
    lotId: new FormControl<string | null>(null),
    dealId: new FormControl<string | null>(null),
    addendumId: new FormControl<string | null>(null),
    taskId: new FormControl<string | null>(null),
    depositTypeId: new FormControl<string | null>(null, [Validators.required]),
    depositDueDate: new FormControl<Date | null>(null, [Validators.required]),
    depositAmount: new FormControl<number | null>(null, [Validators.required]),
    notes: new FormControl<string | null>(null)
  });

  depositPaymentForm = new FormGroup({
    depositId: new FormControl<string | null>(null),
    paymentDate: new FormControl<Date | null>(null, [Validators.required]),
    paymentAmount: new FormControl<number | null>(null, [Validators.required]),
    referenceNo: new FormControl<string | null>(null),
    returned: new FormControl<boolean>(false),
    paymentImageIds: new FormControl<string[]>([])
  });

  readDepositsByLotId(lotId: string): Observable<DsmDeposit[]> {
    return this.#http.getData<DsmDeposit[]>(`${this.#baseUrl}/deposits/lot/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readDepositsByDealId(dealId: string): Observable<DsmDeposit[]> {
    return this.#http.getData<DsmDeposit[]>(`${this.#baseUrl}/deposits/deal/${dealId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  createDeposit(deposit: DsmCreateDeposit[]): Observable<DsmDeposit> {
    return this.#http.postData<DsmDeposit>(
      `${this.#baseUrl}/deposits`,
      { showLoader: true, showMessage: true },
      { deposits: deposit }
    );
  }

  updateDeposit(depositId: string, deposit: DsmDeposit): Observable<DsmDeposit> {
    return this.#http.putData<DsmDeposit>(`${this.#baseUrl}/deposits/${depositId}`, deposit, {
      showLoader: true,
      showMessage: true
    });
  }

  createDepositPayment(depositId: string, depositPayment: DsmCreateDepositPayment): Observable<DsmDepositPayment> {
    depositPayment.returned = depositPayment.returned ?? false;
    return this.#http.postData<DsmDepositPayment>(
      `${this.#baseUrl}/deposits/${depositId}/payment`,
      { showLoader: true, showMessage: true },
      depositPayment
    );
  }

  updateDepositPayment(
    depositId: string,
    paymentId: string,
    depositPayment: DsmCreateDepositPayment
  ): Observable<DsmDepositPayment> {
    return this.#http.putData<DsmDepositPayment>(
      `${this.#baseUrl}/deposits/${depositId}/payment/${paymentId}`,
      depositPayment,
      { showLoader: true, showMessage: true }
    );
  }

  getDepositTypes(): Observable<DropdownData[]> {
    return this.#http
      .getData<DsmDepositType[]>(`${this.#baseUrl}/deposits/types`, { showLoader: true, showMessage: false })
      .pipe(
        map((data: DsmDepositType[] | null | undefined) =>
          (data ?? []).map((item: DsmDepositType) => ({
            id: item.id,
            firstLabel: item.name,
            secondLabel: ''
          }))
        )
      );
  }

  getSummary(lotId: string): Observable<DsmDepositSummary[]> {
    return this.#http.getData<DsmDepositSummary[]>(`${this.#baseUrl}/deposits/lot/${lotId}/summary`, {
      showLoader: true,
      showMessage: false
    });
  }

  getSummaryByDeal(dealId: string): Observable<DsmDepositSummary[]> {
    return this.#http.getData<DsmDepositSummary[]>(`${this.#baseUrl}/deposits/deal/${dealId}/summary`, {
      showLoader: true,
      showMessage: false
    });
  }

  deleteDeposit(depositId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.#baseUrl}/deposits/${depositId}`, {
      showLoader: true,
      showMessage: true
    });
  }

  deleteDepositWithPayments(depositId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.#baseUrl}/deposits/${depositId}/with-payments`, {
      showLoader: true,
      showMessage: true
    });
  }

  deleteDepositPayment(paymentId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.#baseUrl}/deposits/payment/${paymentId}`, {
      showLoader: true,
      showMessage: true
    });
  }

  downloadReceipt(paymentId: string, downloadFileName?: string): Observable<void> {
    return this.#http.downloadReportFile(
      `${this.#baseUrl}/deposits/payment/${paymentId}/receipt`,
      { showLoader: true, showMessage: true },
      undefined,
      downloadFileName
    );
  }

  dealsByDealId(dealId: string): Observable<DropdownData[]> {
    return this.#http
      .getData<DsmDepositLots[]>(`${this.#baseUrl}/deposits/deals/${dealId}/lots`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map((lots: DsmDepositLots[] | null | undefined) =>
          (lots ?? []).map((item: DsmDepositLots) => ({
            id: item.id,
            firstLabel: item.lotNo,
            secondLabel: item.descr
          }))
        )
      );
  }

  earnestByLotId(lotId: string): Observable<DsmImportDeposit[]> {
    return this.#http.getData<DsmImportDeposit[]>(`${this.#baseUrl}/deposits/earnest/lot/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  generateDeposits(
    projectId: string,
    lotId: string,
    dealId: string,
    depScehdulId: string,
    confirmDuplicate = false
  ): Observable<DsmImportDeposit[]> {
    return this.#http.postData<DsmImportDeposit[]>(
      `${this.#baseUrl}/deposit-schedules/generate`,
      { showLoader: true, showMessage: false, showError: false },
      { projectId, lotId, dealId, depositScheduleId: depScehdulId, confirmDuplicate }
    );
  }
}
